import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  CalendarClock,
  Pencil,
  Trash2,
  MoreHorizontal,
  RefreshCw,
  Wand2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { SlotFormDialog } from "@/components/admin/SlotFormDialog";
import { SlotGeneratorDialog } from "@/components/admin/SlotGeneratorDialog";
import { formatTimeRange, formatDateShort, getTodayLocalDate, getLocalDateString } from "@/lib/format/date";

interface Area {
  id: string;
  name: string;
}

interface Slot {
  id: string;
  areaId: string;
  slotType: string;
  startAt: string;
  endAt: string;
  capacity: number;
  bookedCount: number;
  capacityLeft: number;
  area: {
    id: string;
    name: string;
  };
}

const SLOT_TYPES = [
  { value: "", label: "All Types" },
  { value: "MEASUREMENT", label: "Measurement" },
  { value: "PICKUP", label: "Pickup" },
  { value: "DELIVERY", label: "Delivery" },
  { value: "RETURN", label: "Return" },
  { value: "REWORK_PICKUP", label: "Rework Pickup" },
];

const SLOT_TYPE_COLORS: Record<string, string> = {
  MEASUREMENT: "bg-blue-50 text-blue-700 border-blue-200",
  PICKUP: "bg-amber-50 text-amber-700 border-amber-200",
  DELIVERY: "bg-emerald-50 text-emerald-700 border-emerald-200",
  RETURN: "bg-rose-50 text-rose-700 border-rose-200",
  REWORK_PICKUP: "bg-purple-50 text-purple-700 border-purple-200",
};

export default function AdminSlotsPage() {
  const [slots, setSlots] = useState<Slot[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [selectedAreaId, setSelectedAreaId] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedDate, setSelectedDate] = useState(getTodayLocalDate());

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<Slot | null>(null);
  const [deleteSlotId, setDeleteSlotId] = useState<string | null>(null);

  const fetchAreas = async () => {
    try {
      const res = await fetch("/api/admin/areas");
      const data = await res.json();
      if (res.ok) {
        setAreas(data.areas);
      }
    } catch (error) {
      console.error("Error fetching areas:", error);
    }
  };

  const fetchSlots = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedAreaId) params.set("areaId", selectedAreaId);
      if (selectedType) params.set("type", selectedType);
      if (selectedDate) params.set("date", selectedDate);

      const res = await fetch(`/api/admin/slots?${params.toString()}`);
      const data = await res.json();
      if (res.ok) {
        setSlots(data.slots);
      }
    } catch (error) {
      console.error("Error fetching slots:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAreas();
  }, []);

  useEffect(() => {
    fetchSlots();
  }, [selectedAreaId, selectedType, selectedDate]);

  const handleDeleteSlot = async () => {
    if (!deleteSlotId) return;

    try {
      const res = await fetch(`/api/admin/slots/${deleteSlotId}`, {
        method: "DELETE",
      });
      if (res.ok) {
        fetchSlots();
      } else {
        const data = await res.json();
        alert(data.error || "Failed to delete slot");
      }
    } catch (error) {
      console.error("Error deleting slot:", error);
    } finally {
      setDeleteSlotId(null);
    }
  };

  const changeDate = (days: number) => {
    const currentDate = new Date(selectedDate + "T12:00:00");
    currentDate.setDate(currentDate.getDate() + days);
    setSelectedDate(currentDate.toISOString().split("T")[0]);
  };

  const getCapacityColor = (slot: Slot) => {
    const percent = (slot.bookedCount / slot.capacity) * 100;
    if (percent >= 90) return "text-red-600 bg-red-50";
    if (percent >= 70) return "text-amber-600 bg-amber-50";
    return "text-emerald-600 bg-emerald-50";
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Time Slots"
        subtitle="Manage appointment and logistics time slots"
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setIsGeneratorOpen(true)}
            >
              <Wand2 className="h-4 w-4 mr-2" />
              Generator
            </Button>
            <Button
              onClick={() => {
                setEditingSlot(null);
                setIsFormOpen(true);
              }}
              className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Slot
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-4">
        <Select value={selectedAreaId} onValueChange={setSelectedAreaId}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Areas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">All Areas</SelectItem>
            {areas.map((area) => (
              <SelectItem key={area.id} value={area.id}>
                {area.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedType} onValueChange={setSelectedType}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            {SLOT_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => changeDate(-1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-[160px]"
          />
          <Button
            variant="outline"
            size="icon"
            onClick={() => changeDate(1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedDate(getTodayLocalDate())}
          >
            Today
          </Button>
        </div>

        <Button variant="outline" onClick={fetchSlots} disabled={isLoading} className="ml-auto">
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Area</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Capacity</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-8" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : slots.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-12">
          <EmptyState
            icon={CalendarClock}
            title="No slots for this date"
            description="There are no time slots matching your filters. Create slots manually or use the generator."
            action={
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsGeneratorOpen(true)}
                >
                  <Wand2 className="h-4 w-4 mr-2" />
                  Generator
                </Button>
                <Button
                  onClick={() => {
                    setEditingSlot(null);
                    setIsFormOpen(true);
                  }}
                  className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Slot
                </Button>
              </div>
            }
          />
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Time</TableHead>
                <TableHead className="font-semibold">Area</TableHead>
                <TableHead className="font-semibold">Type</TableHead>
                <TableHead className="font-semibold">Capacity</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {slots.map((slot) => (
                <TableRow key={slot.id} className="hover:bg-slate-50/50">
                  <TableCell>
                    <div className="font-medium text-slate-900">
                      {formatTimeRange(slot.startAt, slot.endAt)}
                    </div>
                    <div className="text-xs text-slate-500">
                      {formatDateShort(slot.startAt)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-slate-700">{slot.area.name}</span>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={SLOT_TYPE_COLORS[slot.slotType] || ""}
                    >
                      {SLOT_TYPES.find((t) => t.value === slot.slotType)?.label ||
                        slot.slotType}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div
                        className={`px-2 py-1 rounded text-sm font-medium ${getCapacityColor(slot)}`}
                      >
                        {slot.bookedCount}/{slot.capacity}
                      </div>
                      <span className="text-xs text-slate-500">
                        ({slot.capacityLeft} left)
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            setEditingSlot(slot);
                            setIsFormOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit Capacity
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setDeleteSlotId(slot.id)}
                          className="text-red-600"
                          disabled={slot.bookedCount > 0}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <SlotFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        slot={editingSlot}
        areas={areas}
        onSuccess={fetchSlots}
        defaultAreaId={selectedAreaId}
        defaultDate={selectedDate}
      />

      <SlotGeneratorDialog
        open={isGeneratorOpen}
        onOpenChange={setIsGeneratorOpen}
        areas={areas}
        onSuccess={fetchSlots}
        defaultAreaId={selectedAreaId}
      />

      <AlertDialog open={!!deleteSlotId} onOpenChange={() => setDeleteSlotId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Slot</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this slot? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSlot}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
